/**
 * IndividualOpsView
 *
 * Paginated table of individual operation records.
 */

import { useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import type { IndividualOperation, UserBreakdown } from '@/services/metricsAPI';

interface IndividualOpsViewProps {
  ops: IndividualOperation[];
  loading: boolean;
  cursor: string | null;
  onLoadMore: () => void;
}

const formatSeconds = (s: number): string => {
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${(s % 60).toFixed(0)}s`;
};

const formatDate = (d: string): string => new Date(d).toLocaleString();

const formatUser = (op: IndividualOperation | UserBreakdown): string => {
  if ('user_display_name' in op && op.user_display_name) return op.user_display_name;
  if ('user_email' in op && op.user_email) return op.user_email;
  if ('user_id' in op && op.user_id) return op.user_id;
  return 'Unknown';
};

export function IndividualOpsView({
  ops,
  loading,
  cursor,
  onLoadMore,
}: IndividualOpsViewProps): JSX.Element {
  const { t } = useTranslation();
  const [expandedOpId, setExpandedOpId] = useState<string | null>(null);

  return (
    <div className="panel-glass-card-lg">
      <h2 className="section-heading-lg">{t('admin.metrics.individualView')}</h2>

      {loading && ops.length === 0 ? (
        <div className="text-white/50 text-center py-8">{t('common.loading')}</div>
      ) : ops.length === 0 ? (
        <div className="text-white/50 text-center py-8">{t('admin.metrics.noData')}</div>
      ) : (
        <>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full text-sm text-white/80 min-w-[1000px]">
              <thead className="text-white/50 border-b border-white/10">
                <tr>
                  <th className="text-left py-3 px-3">{t('admin.metrics.timestamp')}</th>
                  <th className="text-left py-3 px-3">{t('admin.metrics.user')}</th>
                  <th className="text-left py-3 px-3">{t('admin.metrics.operation')}</th>
                  <th className="text-left py-3 px-3">{t('admin.metrics.provider')}</th>
                  <th className="text-right py-3 px-3">{t('admin.metrics.duration')}</th>
                  <th className="text-right py-3 px-3">{t('admin.metrics.tokens')}</th>
                  <th className="text-center py-3 px-3">{t('admin.metrics.country')}</th>
                  <th className="text-center py-3 px-3">{t('admin.metrics.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {ops.map((op) => (
                  <>
                    <tr
                      key={op.id}
                      className="hover:bg-white/5 cursor-pointer"
                      onClick={() => setExpandedOpId(expandedOpId === op.id ? null : op.id)}
                    >
                      <td className="py-3 px-3 text-xs">{formatDate(op.created_at)}</td>
                      <td className="py-3 px-3">{formatUser(op)}</td>
                      <td className="py-3 px-3 font-medium">{op.operation}</td>
                      <td className="py-3 px-3">{op.provider}</td>
                      <td className="py-3 px-3 text-right">
                        {formatSeconds(op.processing_seconds)}
                      </td>
                      <td className="py-3 px-3 text-right">{op.tokens_charged.toLocaleString()}</td>
                      <td className="py-3 px-3 text-center">{op.country_code ?? '-'}</td>
                      <td className="py-3 px-3 text-center">
                        {op.success ? (
                          <span className="text-green-400">OK</span>
                        ) : (
                          <span className="text-red-400">FAIL</span>
                        )}
                      </td>
                    </tr>
                    {expandedOpId === op.id && (
                      <tr key={`${op.id}-detail`}>
                        <td colSpan={8} className="p-0">
                          <div className="bg-black/20 border-t border-b border-purple-500/20 p-4">
                            <h4 className="text-sm font-semibold text-white/80 mb-2">
                              {t('admin.metrics.inputParams')}
                            </h4>
                            <pre className="text-xs text-white/70 bg-black/30 p-3 rounded overflow-x-auto">
                              {JSON.stringify(op.input_params, null, 2)}
                            </pre>
                            {op.error_message && (
                              <>
                                <h4 className="text-sm font-semibold text-white/80 mb-2 mt-4">
                                  Error Message
                                </h4>
                                <pre className="text-xs text-red-400 bg-black/30 p-3 rounded overflow-x-auto">
                                  {op.error_message}
                                </pre>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {cursor && (
            <div className="mt-6 text-center">
              <button
                onClick={onLoadMore}
                disabled={loading}
                className="px-6 py-3 bg-purple-600/30 text-purple-300 border border-purple-500/50 rounded-lg hover:bg-purple-600/40 transition-colors disabled:opacity-50 min-h-[44px] min-w-[120px]"
              >
                {loading ? t('common.loading') : t('admin.metrics.loadMore')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
